/**
 * Turn a raw /status-events row into the two lines the NotificationDrawer
 * renders: a title (main sentence) and a subtitle (contextual line —
 * usually the client / company / route / amount).
 *
 * Copy follows the design-mockup patterns:
 *   - Flights          → "Tail {N###} is now in transit." · "Flight for {client}"
 *   - Client actions   → "{Client} verbed …" · "{company}"
 *   - Merchant capture → "Payment for {TR-…} has been captured at the
 *                        payment merchant." · "{TR-…}"
 *
 * Duplicate signals: on a successful payment the BE emits BOTH a
 * client-facing row (quotes/invoices.payment_status:CAPTURED) and a
 * system-facing row (payments.status:CAPTURED). Both surface in the
 * feed with different copy — matches the mockup where both are shown.
 *
 * When a combination isn't in the lookup below we fall back to a
 * deterministic "{Category} status updated to {STATUS}." line so
 * unknown BE additions never render blank cards.
 */

const hasClient = (client) => {
  if (!client) return false;
  const name = [client.first_name, client.last_name].filter(Boolean).join(' ').trim();
  return !!(name || client.company_name || client.email);
};

const clientLabel = (client) => {
  if (!client) return 'A client';
  const name = [client.first_name, client.last_name].filter(Boolean).join(' ').trim();
  return name || client.company_name || client.email || 'A client';
};

const companyLabel = (client) => client?.company_name || '';

const quoteLabel = (quote) => quote?.quote_number || 'the quote';
const invoiceLabel = (invoice) => invoice?.invoice_number || 'the invoice';
const routeLabel = (o) => {
  const dep = o?.departure_airport;
  const arr = o?.arrival_airport;
  if (!dep && !arr) return '';
  return `${dep || '?'} → ${arr || '?'}`;
};
const tailLabel = (flight) => flight?.aircraft?.tail_number || flight?.tail_number || 'the tail';

/**
 * "$135.96" — prefers the payment record's currency symbol/code (USD →
 * "$", EUR → "€"), falls back to a plain "$" when the currency is
 * unknown or the amount can't be parsed.
 */
const CURRENCY_SYMBOLS = { USD: '$', CAD: '$', AUD: '$', EUR: '€', GBP: '£', JPY: '¥' };
const formatAmount = (amount, currency) => {
  const n = parseFloat(amount);
  if (!Number.isFinite(n)) return '';
  const sym = CURRENCY_SYMBOLS[String(currency || '').toUpperCase()] || '$';
  return `${sym}${n.toFixed(2)}`;
};

/**
 * Join subtitle pieces with " · " and drop anything empty. Keeps the
 * output tight when e.g. company is missing on a client action.
 */
const joinSub = (...parts) => parts.filter(Boolean).join(' · ');

/**
 * Format a raw enum like SETTLED_VIA_PASSPORT → "Settled Via Passport".
 * Only used by the deterministic fallback so unknown transitions still
 * read cleanly.
 */
const humanizeStatus = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Return `{ title, subtitle }` for the given event. The map key is
 * `${category}:${source_column}:${status}`; when a single key needs
 * different copy depending on the transition (retry vs first attempt,
 * reopen vs first sign) we branch inside the case on `oldStatus`.
 */
export const getNotificationCopy = (event) => {
  if (!event) return { title: 'Notification', subtitle: '' };
  const { category, source_column: source, status, old_status: oldStatus, context = {} } = event;
  const key = `${category}:${source}:${status}`;
  const clientPresent = hasClient(context.client);
  const client = clientLabel(context.client);
  const company = companyLabel(context.client);
  const quoteNo = quoteLabel(context.quote);
  const invoiceNo = invoiceLabel(context.invoice);
  const route = routeLabel(context.flight) || routeLabel(context.quote);
  const tail = tailLabel(context.flight);

  // Money formatting — prefer the payment record's currency, else fall
  // back to whatever the parent invoice/quote total is (plain "$").
  const total = formatAmount(
    context.payment?.amount ?? context.invoice?.total_amount ?? context.quote?.total_due,
    context.payment?.currency,
  );

  switch (key) {
    // ── Quotes: status column ──────────────────────────────────────────────
    case 'quotes:status:SENT':
      // The only observed old_status for this is COMPLETED (a re-send
      // after a trip). Keep the copy explicit so it's not confused with
      // the original "quote sent" signal.
      return { title: `Quote ${quoteNo} was re-sent to ${client}.`, subtitle: joinSub(company, route) };
    case 'quotes:status:VIEWED':
      return { title: `${client} viewed quote ${quoteNo}.`, subtitle: company };
    case 'quotes:status:SIGNED':
      if (oldStatus === 'COMPLETED') {
        return { title: `Quote ${quoteNo} was re-opened for signature.`, subtitle: clientPresent ? client : '' };
      }
      return { title: `${client} signed quote ${quoteNo}.`, subtitle: company };
    case 'quotes:status:ACCEPTED':
      return { title: `${client} accepted quote ${quoteNo}.`, subtitle: company };
    case 'quotes:status:DECLINED':
      return { title: `${client} declined quote ${quoteNo}.`, subtitle: company };
    case 'quotes:status:EXPIRED':
      return {
        title: oldStatus === 'SIGNED'
          ? `Quote ${quoteNo} expired before payment.`
          : `Quote ${quoteNo} expired.`,
        subtitle: clientPresent ? client : '',
      };
    case 'quotes:status:IN_TRANSIT':
      return { title: `Quote ${quoteNo} is now in transit.`, subtitle: clientPresent ? `Flight for ${client}` : route };
    case 'quotes:status:COMPLETED':
      return { title: `Trip completed for quote ${quoteNo}.`, subtitle: clientPresent ? client : '' };

    // ── Quotes: client-submitted passenger details ────────────────────────
    // The broker sends a passenger-details request to the client (status=1,
    // typically emitted for the broker's own action — we skip it). When the
    // client fills in and submits the form, BE flips the row to status=2
    // and emits this event to the broker.
    // Note: status is a STRING here ('2'), not a number — the BE serializes
    // it as such. The source column is dotted (files.request_client_
    // passengers) because it points at a JSON key on the quote's files blob.
    case 'quotes:files.request_client_passengers:2':
      return {
        title: `${client} submitted passenger details for ${quoteNo}.`,
        subtitle: company,
      };

    // ── Quotes: payment_status column (mockup card 2 pattern) ──────────────
    case 'quotes:payment_status:CAPTURED':
      return {
        title: `${client} has completed the payment for ${quoteNo}.`,
        subtitle: company,
      };
    case 'quotes:payment_status:SETTLED_VIA_PASSPORT':
      return { title: `Quote ${quoteNo} was settled via Passport.`, subtitle: clientPresent ? client : '' };

    // ── Invoices ───────────────────────────────────────────────────────────
    case 'invoices:status:SENT':
      return { title: `Invoice ${invoiceNo} was sent to ${client}.`, subtitle: company };
    case 'invoices:payment_status:CAPTURED':
      return {
        title: `${client} has completed the payment for invoice ${invoiceNo}.`,
        subtitle: joinSub(total, company),
      };
    case 'invoices:payment_status:SETTLED_VIA_PASSPORT':
      return { title: `Invoice ${invoiceNo} was settled via Passport.`, subtitle: clientPresent ? client : '' };

    // ── Payments (mockup card 3 pattern lives on CAPTURED-from-AUTHORIZED) ─
    case 'payments:status:AUTHORIZED': {
      // Prefer the parent quote/invoice number for the "authorized for X" line.
      const parentRef = context.quote?.quote_number || context.invoice?.invoice_number || null;
      if (oldStatus === 'FAILED') {
        return { title: `Payment reauthorized after retry.`, subtitle: joinSub(clientPresent ? client : '', total) };
      }
      if (oldStatus === 'CAPTURED') {
        return { title: `Payment reauthorized.`, subtitle: joinSub(clientPresent ? client : '', total) };
      }
      return {
        title: parentRef ? `Payment authorized for ${parentRef}.` : `Payment authorized.`,
        subtitle: joinSub(clientPresent ? client : '', total),
      };
    }
    case 'payments:status:CAPTURED': {
      const parentRef = context.quote?.quote_number || context.invoice?.invoice_number || null;
      if (oldStatus === 'FAILED') {
        return { title: `Payment captured after retry.`, subtitle: joinSub(clientPresent ? client : '', total) };
      }
      // Design mockup card 3 exact phrasing.
      return {
        title: parentRef
          ? `Payment for ${parentRef} has been captured at the payment merchant.`
          : `Payment has been captured at the payment merchant.`,
        subtitle: parentRef || joinSub(clientPresent ? client : '', total),
      };
    }
    case 'payments:status:FAILED':
      return {
        title: oldStatus === 'REFUNDED' ? `Refund failed.` : `Payment failed.`,
        subtitle: joinSub(clientPresent ? client : '', total),
      };
    case 'payments:status:RELEASED':
      return { title: `Payment hold was released.`, subtitle: joinSub(clientPresent ? client : '', total) };
    case 'payments:status:REFUNDED':
      return {
        title: clientPresent ? `Payment refunded to ${client}.` : `Payment refunded.`,
        subtitle: total,
      };

    // ── Flights: flight_status column (mockup card 1 pattern) ──────────────
    case 'flights:flight_status:READY':
      return { title: `Tail ${tail} is ready for departure.`, subtitle: clientPresent ? `Flight for ${client}` : route };
    case 'flights:flight_status:POSITIONING':
      return { title: `Tail ${tail} is positioning.`, subtitle: clientPresent ? `Flight for ${client}` : route };
    case 'flights:flight_status:IN_TRANSIT':
      return { title: `Tail ${tail} is now in transit.`, subtitle: clientPresent ? `Flight for ${client}` : route };
    case 'flights:flight_status:DELAYED':
      return { title: `Tail ${tail} is delayed.`, subtitle: clientPresent ? `Flight for ${client}` : route };
    case 'flights:flight_status:LAYOVER':
      return { title: `Tail ${tail} is on layover.`, subtitle: clientPresent ? `Flight for ${client}` : route };
    case 'flights:flight_status:COMPLETED':
      return { title: `Tail ${tail} has completed its flight.`, subtitle: clientPresent ? `Flight for ${client}` : route };
    case 'flights:flight_status:CANCELLED':
      return { title: `Tail ${tail} was cancelled.`, subtitle: clientPresent ? `Flight for ${client}` : route };

    default:
      // Deterministic fallback so unknown combinations still render a
      // card (rather than a blank line). Reads as e.g.
      // "Quotes status updated to Accepted."
      return {
        title: `${humanizeStatus(category)} ${source === 'status' ? 'status' : humanizeStatus(source)} updated to ${humanizeStatus(status)}.`,
        subtitle: joinSub(
          clientPresent ? client : '',
          context.quote?.quote_number,
          context.invoice?.invoice_number,
        ),
      };
  }
};

/**
 * Which UI-level bucket does this event belong to? Drives icon choice
 * AND the filter-pill mapping (Financials unions payments + invoices
 * since the BE splits them but the broker mental-models them together).
 */
export const notificationBucket = (event) => {
  const c = event?.category;
  if (c === 'flights') return 'flight';
  if (c === 'quotes') return 'quote';
  if (c === 'payments' || c === 'invoices') return 'financial';
  return 'other';
};
